import { OverlayRef } from '@angular/cdk/overlay';
import { Observable, Subject } from 'rxjs';

/**
 * Reference to a snackbar instance. Returned by HxSnackbarService.show() method. Provides programmatic control and
 * lifecycle observables.
 */
export class HxSnackbarRef<T = any> {
  /** Subject for dismissal notifications */
  private readonly _afterDismissed = new Subject<void>();

  /** Subject for afterOpened notifications */
  private readonly _afterOpened = new Subject<void>();

  /** Flag to prevent multiple dismiss calls */
  private _dismissed = false;

  /** Reference to the snackbar component instance */
  instance: any;

  /** Custom data passed to snackbar */
  data?: T;

  /**
   * CDK OverlayRef for positioning control
   *
   * @internal
   */
  readonly _overlayRef: OverlayRef;

  /**
   * Container instance managing this snackbar
   *
   * @internal
   */
  readonly _containerInstance: any;

  /**
   * Auto-dismiss timer reference
   *
   * @internal
   */
  _dismissTimer?: ReturnType<typeof setTimeout>;

  /** Original auto-dismiss duration in ms (service mode) */
  _dismissDurationMs?: number;

  /** Remaining auto-dismiss time in ms when paused */
  _remainingDismissMs?: number;

  /** Last timer start timestamp (epoch ms) */
  _dismissTimerStartedAt?: number;

  constructor(overlayRef: OverlayRef, containerInstance: any) {
    this._overlayRef = overlayRef;
    this._containerInstance = containerInstance;
  }

  /** Manually dismiss the snackbar immediately. Triggers exit animation and cleanup. */
  dismiss(): void {
    if (this._dismissed) {
      return;
    }

    this._dismissed = true;

    // Clear auto-dismiss timer if exists
    if (this._dismissTimer) {
      clearTimeout(this._dismissTimer);
      this._dismissTimer = undefined;
    }
    this._remainingDismissMs = undefined;
    this._dismissDurationMs = undefined;
    this._dismissTimerStartedAt = undefined;

    // Trigger exit animation via component
    if (this.instance && typeof this.instance.startExit === 'function') {
      this.instance.startExit();
    }
  }

  /** Observable that emits when snackbar is dismissed. Emits once and completes immediately after. */
  afterDismissed(): Observable<void> {
    return this._afterDismissed.asObservable();
  }

  /**
   * Complete afterDismissed observable and cleanup. Called internally after exit animation completes.
   *
   * @internal
   */
  _finishDismiss(): void {
    // Emit dismissal event
    this._afterDismissed.next();
    this._afterDismissed.complete();

    // Notify container to destroy component and process queue
    if (this._containerInstance && typeof this._containerInstance.onSnackbarDismissed === 'function') {
      this._containerInstance.onSnackbarDismissed(this);
    }
  }

  /**
   * Update snackbar position (for stacking).
   *
   * @param offsetTop - Top offset in pixels
   * @internal
   */
  _updatePosition(offsetTop: number): void {
    if (this._overlayRef) {
      const positionStrategy = this._overlayRef.getConfig().positionStrategy;
      if (positionStrategy && typeof (positionStrategy as any).top === 'function') {
        (positionStrategy as any).top(`${offsetTop}px`);
        this._overlayRef.updatePosition();
      }
    }
  }

  /** Observable that emits when the snackbar has finished entering the view. */
  afterOpened(): Observable<void> {
    return this._afterOpened.asObservable();
  }

  /**
   * Mark the snackbar as opened. Called internally by the container after enter animation completes.
   *
   * @internal
   */
  _open(): void {
    if (!this._afterOpened.closed) {
      this._afterOpened.next();
      this._afterOpened.complete();
    }
  }

  /** @internal Start service-mode auto-dismiss timer */
  _startAutoDismissTimer(durationMs: number): void {
    if (durationMs <= 0) {
      return;
    }

    this._dismissDurationMs = durationMs;
    this._remainingDismissMs = durationMs;
    this._dismissTimerStartedAt = Date.now();

    if (this._dismissTimer) {
      clearTimeout(this._dismissTimer);
    }

    this._dismissTimer = setTimeout(() => {
      this.dismiss();
    }, durationMs);
  }

  /** @internal Pause service-mode auto-dismiss timer */
  _pauseAutoDismissTimer(): void {
    if (!this._dismissTimer || !this._dismissTimerStartedAt) {
      return;
    }

    const elapsedMs = Date.now() - this._dismissTimerStartedAt;
    const baseMs = this._remainingDismissMs ?? this._dismissDurationMs ?? 0;
    this._remainingDismissMs = Math.max(0, baseMs - elapsedMs);
    clearTimeout(this._dismissTimer);
    this._dismissTimer = undefined;
    this._dismissTimerStartedAt = undefined;
  }

  /** @internal Resume service-mode auto-dismiss timer using remaining time */
  _resumeAutoDismissTimer(): void {
    const remainingMs = this._remainingDismissMs ?? this._dismissDurationMs;
    if (!remainingMs || remainingMs <= 0 || this._dismissTimer) {
      return;
    }

    this._dismissTimerStartedAt = Date.now();
    this._dismissTimer = setTimeout(() => {
      this.dismiss();
    }, remainingMs);
  }

  /** @internal Restart service-mode auto-dismiss timer from full duration */
  _restartAutoDismissTimer(): void {
    const durationMs = this._dismissDurationMs;
    if (!durationMs || durationMs <= 0) {
      return;
    }
    this._startAutoDismissTimer(durationMs);
  }
}
